import { useCompanyListQuery } from "Company/Api/Query/useCompanyListQuery";
import { useCompanyBankDetailListQuery } from "Company/Modules/BankDetail/Api/Query/useCompanyBankDetailListQuery";
import {
	CountryEnum,
	CurrencyEnum,
	type MarketResponseDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { useCreateMarketMutation } from "Market/Api/Mutation/createMarketMutation";
import {
	type GeneralFormSchema,
	getGeneralFormSchema,
} from "Market/Components/Form/Schema/generalFormSchema";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronRight } from "@mui/icons-material";
import { Button, Grid2, Stack } from "@mui/material";
import { isEmpty } from "lodash";
import moment from "moment-timezone";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

type GeneralFormProps = {
	onSuccess: (response: MarketResponseDto) => void;
};

const defaultFormValues = {
	name: "",
	country: CountryEnum.CZE,
	currency: CurrencyEnum.CZK,
	webReferrer: "",
	urlPath: "",
	iconPath: "",
	imagePath: "",
	locale: "en",
	timezone: "",
};

const marketFormResource = Resources.base.market.forms;

export const GeneralForm = ({ onSuccess }: GeneralFormProps) => {
	const { t } = useResource();

	const form = useForm<GeneralFormSchema>({
		resolver: zodResolver(getGeneralFormSchema(t)),
		defaultValues: defaultFormValues,
	});

	const {
		watch,
		control,
		formState: { errors },
		setValue,
	} = form;

	const { mutate: createMarket, isPending } = useCreateMarketMutation();

	const selectedCompanyId = watch("companyId");
	const selectedCountry = watch("country");
	const { data: companyList } = useCompanyListQuery();

	const {
		data: companyBankDetailList,
		refetch: refetchCompanyBankDetails,
		isLoading: isLoadingCompanyBankDetails,
	} = useCompanyBankDetailListQuery(selectedCompanyId ?? 0);

	useEffect(() => {
		setValue("companyBankDetailId", undefined);
		selectedCompanyId && refetchCompanyBankDetails();
	}, [selectedCompanyId, setValue]);

	useEffect(() => {
		if (selectedCountry) {
			setValue("urlPath", selectedCountry.toLowerCase());
		}
	}, [selectedCountry, setValue]);

	const { handleServerError } = useHandleServerError(form);

	const handleSubmitCreate = () => {
		handleServerError(createMarket, {
			onSuccess: (response) => {
				onSuccess(response);
				form.reset(defaultFormValues);
			},
		});
	};

	const timezoneOptions = useMemo(() => {
		const timezones = moment.tz.names();
		return timezones.map((tz) => ({
			label: tz,
			value: tz,
		}));
	}, []);

	return (
		<form style={{ marginTop: 0 }}>
			<Grid2 container columnSpacing={2} mb={2}>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Input
						id="name"
						name="name"
						label={t(marketFormResource.labels.name)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Select
						id="country"
						name="country"
						label={t(marketFormResource.labels.country)}
						variant="outlined"
						control={control}
						errors={errors}
						options={createSelectOptions(CountryEnum)}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Select
						id="companyId"
						name="companyId"
						label={t(marketFormResource.labels.company)}
						variant="outlined"
						control={control}
						errors={errors}
						options={
							companyList?.map((m) => {
								return {
									label: m.name,
									value: m.id,
								};
							}) ?? []
						}
						fullWidth
						numeric
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Select
						id="companyBankDetailId"
						name="companyBankDetailId"
						label={t(marketFormResource.labels.companyBankDetail)}
						variant="outlined"
						control={control}
						errors={errors}
						options={
							companyBankDetailList?.map((m) => {
								return {
									label: m.name,
									value: m.id,
								};
							}) ?? []
						}
						disabled={!selectedCompanyId || isLoadingCompanyBankDetails}
						isLoading={isLoadingCompanyBankDetails}
						numeric
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Select
						id="currency"
						name="currency"
						label={t(marketFormResource.labels.currency)}
						variant="outlined"
						control={control}
						errors={errors}
						options={createSelectOptions(CurrencyEnum)}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Select
						id="timezone"
						name="timezone"
						label={t(marketFormResource.labels.timezone)}
						variant="outlined"
						control={control}
						errors={errors}
						options={timezoneOptions}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Input
						id="web-referrer"
						name="webReferrer"
						label={t(marketFormResource.labels.webReferrer)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Input
						id="url-path"
						name="urlPath"
						label={t(marketFormResource.labels.urlPath)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled
					/>
				</Grid2>
			</Grid2>
			<Stack flexDirection="row" justifyContent="flex-end">
				<Button
					variant="contained"
					onClick={handleSubmitCreate}
					loading={isPending}
					disabled={!isEmpty(errors)}
					endIcon={<ChevronRight fontSize="small" />}
				>
					{t(marketFormResource.next)}
				</Button>
			</Stack>
		</form>
	);
};
